import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
    GetRelationshipRequest,
    GetRelationshipService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { SavedDateFilterService } from '../../services/saved-date-filter.service';
import { ICustomerDetailBannerData } from './customer-detail-banner-data';
import { RelationshipInformation } from '@lsq-frontend/remote-services/enterprise/common';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

/**
 * This service acts as a controller for the customer currently being viewed.
 */
@Injectable()
export class CustomerDetailService {
    private _relationshipInformationSubject = new ReplaySubject<RelationshipInformation>(1);
    private _bannerDataSubject = new BehaviorSubject<Partial<ICustomerDetailBannerData>>({});
    public relationshipInformationStream = this._relationshipInformationSubject.pipe(
        filter((data) => !!data)
    );

    public bannerDataStream = combineLatest(
        this._bannerDataSubject,
        this._relationshipInformationSubject.pipe(
            filter((data) => !!data),
            map(({ debtorKey, ...rest }) => rest)
        )
    ).pipe(
        filter((data) => !!data),
        map(
            ([bannerData, relationshipInformation]) =>
                ({
                    ...bannerData,
                    title: relationshipInformation.debtorName,
                    debtorName: relationshipInformation.debtorName
                } as ICustomerDetailBannerData)
        )
    );

    public bannerData: ICustomerDetailBannerData;
    public bannerContentTemplateKey = Symbol('bannerContentTemplate');
    public hideExport = false;

    constructor(
        private _alertService: AlertService,
        private _savedDateFilterService: SavedDateFilterService,
        private _getRelationshipService: GetRelationshipService,
        private _location: Location
    ) {
        this.bannerDataStream
            .pipe(
                filter((bannerData) => !!bannerData) // Only take it if the title is truthy.
            )
            .subscribe((bannerData) => {
                this.bannerData = bannerData;
            });
    }

    public initCustomer(debtorKey: number): void {
        // Execute the GetRelationshipService.
        this._getRelationshipService
            .executeOnce(new GetRelationshipRequest({ debtorKey }))
            .then(({ relationshipInformation }) => {
                this.updateBanner({ debtorKey });
                this._relationshipInformationSubject.next(relationshipInformation);
            })
            .catch(() => {
                this._alertService.error(
                    'There was an issue finding the selected customer relationship.'
                );
                this._location.back();
            });

        // Reset saved customer report date filters
        this._savedDateFilterService.clearCustomerReports();
    }

    public updateBanner(bannerData: Partial<ICustomerDetailBannerData>): void {
        const _bannerData = this._bannerDataSubject.getValue();
        this._bannerDataSubject.next({
            ..._bannerData,
            ...bannerData
        });
    }
}
